import NiceModal from '@ebay/nice-modal-react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import DateInput from 'components/field/DateInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { REPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faDownload } from '@fortawesome/pro-regular-svg-icons';

/**
 * @author @muhammedburakak
 */

const PaidCODDetails = NiceModal.create(({ ID }) => {
	const { showError } = useAlert();
	const axios = useAxiosPrivate();
	const colSpan = useMemo(() => ({ md: 6 }), []);
	const [loading, setLoading] = useState(true);
	const formMethods = useForm();
	const { reset, watch } = formMethods;

	const fillForm = useCallback(async () => {
		setLoading(true);

		await axios
			.post(REPORT.LIST.GET_PAID_COD_DETAIL, { ID })
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						...data
					});
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
		setLoading(false);
	}, [ID]);
	useEffect(() => {
		ID && fillForm();
	}, [ID]);

	const handleClick = async (data) => {
		try {
			const url = `https://login.tryoto.com/FileDownload?path=${data}`;
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal maxWidth="x-large" fullwidth className="modal-paidCODDetails">
			<ModalTitle>
				<Typography variant="p1" noWrap>
					paidCODDetails
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} viewPrintButton>
					<FormItem label="startDate" colSpan={colSpan} isLoading={loading}>
						<DateInput name="startDate" value={watch('startDate')} disabled />
					</FormItem>
					<FormItem label="endDate" colSpan={colSpan} isLoading={loading}>
						<DateInput name="endDate" value={watch('endDate')} disabled />
					</FormItem>
					<FormItem label="totalAmount" colSpan={colSpan} isLoading={loading}>
						<CurrencyInput name="totalAmount" disabled />
					</FormItem>
					<FormItem label="paymentReferenceCopy" colSpan={colSpan} isLoading={loading}>
						<Button
							label="download"
							variant="blueBorderless"
							icon={faDownload}
							disabled={!watch('paymentReferenceCopy')}
							onClick={() => handleClick(watch('paymentReferenceCopy'))}
						/>
					</FormItem>

					<FormItem>
						<PaidCODOrdersList ID={ID} startDate={watch('startDate')} endDate={watch('endDate')} />
					</FormItem>
				</Form>
			</ModalContent>
		</Modal>
	);
});

export default PaidCODDetails;

const PaidCODOrdersList = ({ ID, startDate, endDate }) => {
	const { getLocaleDate } = useLocale();

	const translationObject = useMemo(
		() => ({
			startDate: getLocaleDate(startDate),
			endDate: getLocaleDate(endDate)
		}),
		[startDate, endDate]
	);
	const defaultFilters = useMemo(
		() => [
			{
				column: { Header: 'CODReportID', accessor: 'CODReportID', filterType: FilterType.number },
				action: FilterAction.is,
				value: ID
			}
		],
		[]
	);
	const columns = useMemo(
		() => [
			{ Header: 'orderID', accessor: 'orderIncrementID', filterType: FilterType.number, showOnMobile: true },
			{ Header: 'shipmentNumber', accessor: 'shipmentNumber', filterType: FilterType.string },
			{ Header: 'deliveryCompany', accessor: 'dcName', filterType: FilterType.multiSelection },
			{
				Header: 'amount',
				accessor: 'amount',
				filterType: FilterType.string,
				renderCell: ListComponents.CURRENCY
			},
			{ Header: 'description', accessor: 'description', filterType: FilterType.string },
			{
				Header: 'status',
				accessor: 'status',
				renderCell: ListComponents.STATUS,
				filterType: FilterType.multiSelection,
				showOnMobile: true
			}
		],
		[]
	);
	return (
		<div>
			<DynamicList
				id={DYNAMICLISTS.PAID_COD_ORDERS_LIST}
				columns={columns}
				fetchUrl={REPORT.LIST.GET_PAID_COD_DETAIL_LIST}
				exportName="paidCODDetailsWithDate"
				defaultFilters={defaultFilters}
				showHeader={false}
				showHeaderFilters={false}
				showFilters={false}
				exportNameTranslationObject={translationObject}
			/>
		</div>
	);
};
